/**
 * Centralized Icon component using Lucide React
 * Provides consistent icon rendering with size and color variants
 */

import React, { FC } from 'react';
import * as LucideIcons from 'lucide-react';

/**
 * Unified Icon component
 * - By default, renders Lucide icons (name must be a Lucide export)
 * - If `provider` is set to 'asset' or an asset with the given name exists in src/assets/svg,
 *   the component will render the project SVG (inline if `?react` is available, otherwise an <img> fallback).
 */

// Discover available Lucide icon names
export type LucideIconName = keyof typeof LucideIcons;

// Build a map of project SVG assets using Vite's import.meta.glob
const rawSvgs = import.meta.glob('/src/assets/svg/*.svg', { eager: true, as: 'url' }) as Record<
  string,
  string
>;
const reactSvgs = import.meta.glob('/src/assets/svg/*.svg?react', { eager: true }) as Record<
  string,
  { default: FC<React.SVGProps<SVGSVGElement>> }
>;

// Normalize into a map keyed by filename (without extension)
const ASSET_REGISTRY: Record<
  string,
  { component?: FC<React.SVGProps<SVGSVGElement>>; url?: string }
> = {};
for (const p in rawSvgs) {
  const name = p
    .split('/')
    .pop()!
    .replace(/\.svg$/i, '');
  ASSET_REGISTRY[name] = ASSET_REGISTRY[name] || {};
  ASSET_REGISTRY[name]!.url = rawSvgs[p];
}
for (const p in reactSvgs) {
  const filename = p.split('/').pop()!; // e.g. v.svg?react
  const name = filename.replace(/\.svg\?react$/i, '');
  ASSET_REGISTRY[name] = ASSET_REGISTRY[name] || {};
  ASSET_REGISTRY[name]!.component = (reactSvgs as any)[p].default;
}

export type AssetIconName = keyof typeof ASSET_REGISTRY;
export type IconName = LucideIconName | AssetIconName;

export interface IconProps {
  // name can be either a Lucide icon name or an asset filename
  name: string | LucideIconName | AssetIconName;
  provider?: 'lucide' | 'asset' | 'auto'; // auto will try assets first then lucide
  size?: number | string;
  color?: string;
  className?: string;
  strokeWidth?: number;
  'aria-label'?: string;
}

export const Icon: FC<IconProps> = ({
  name,
  provider = 'lucide',
  size = 16,
  color,
  className = '',
  strokeWidth = 2,
  'aria-label': ariaLabel,
}) => {
  const strName = String(name);

  // If provider says asset, or auto and asset exists, render asset
  const assetEntry = ASSET_REGISTRY[strName] || ASSET_REGISTRY[strName.toLowerCase()];
  const shouldUseAsset = provider === 'asset' || (provider === 'auto' && !!assetEntry);

  if (shouldUseAsset && assetEntry) {
    // prefer inline react component
    if (assetEntry.component) {
      const SvgComp = assetEntry.component as FC<React.SVGProps<SVGSVGElement>>;
      const sizeProps: any =
        typeof size === 'number' ? { width: size, height: size } : { width: size, height: size };
      return (
        <SvgComp
          {...sizeProps}
          className={className}
          {...(ariaLabel ? { title: ariaLabel } : {})}
        />
      );
    }

    if (assetEntry.url) {
      return (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img
          src={assetEntry.url}
          width={size as any}
          height={size as any}
          className={className}
          alt={ariaLabel || strName}
        />
      );
    }
  }

  // Fallback to lucide icons
  const LucideIcon = (LucideIcons as any)[strName] as LucideIcons.LucideIcon | undefined;
  if (LucideIcon) {
    return (
      <LucideIcon
        size={size}
        color={color}
        className={className}
        strokeWidth={strokeWidth}
        aria-label={ariaLabel || strName}
        aria-hidden={!ariaLabel}
      />
    );
  }

  if (process.env.NODE_ENV !== 'production') {
    console.warn(`Icon: could not find icon or asset named "${strName}"`);
  }

  return null;
};

export default Icon;
